import {
    CalculatorPresentationContractError,
    requirePresentationArray,
    requirePresentationFields,
    requirePresentationFinite,
    requirePresentationRecord,
    requirePresentationString,
} from "./calculator-presentation-contract.js";
import {
    calculatorDataText as t,
} from "./calculator-data-i18n.js";

const U32_MAX = 4_294_967_295;

function requireInteger(value, path) {
    const parsed = requirePresentationFinite(value, path, {
        minimum: 0,
        maximum: U32_MAX,
    });
    if (!Number.isInteger(parsed)) {
        throw new CalculatorPresentationContractError(path, "expected an unsigned integer");
    }
    return parsed;
}

function parseUnavailableReason(value, path) {
    const reason = requirePresentationFields(
        value,
        ["code", "detailCode", "sourceId"],
        path,
    );
    requirePresentationString(reason.code, `${path}.code`);
    for (const field of ["detailCode", "sourceId"]) {
        if (reason[field] !== null) {
            requirePresentationString(reason[field], `${path}.${field}`);
        }
    }
    return reason;
}

export function parseCalculatorItemQuantityV1(value, path = "quantity") {
    const quantity = requirePresentationRecord(value, path);
    if (quantity.state === "known") {
        const known = requirePresentationFields(
            quantity,
            ["maximum", "minimum", "state"],
            path,
        );
        const minimum = requireInteger(known.minimum, `${path}.minimum`);
        const maximum = requireInteger(known.maximum, `${path}.maximum`);
        if (maximum < minimum) {
            throw new CalculatorPresentationContractError(
                `${path}.maximum`,
                "must be at least the minimum",
            );
        }
        return Object.freeze({ state: "known", minimum, maximum });
    }
    if (quantity.state === "unknown") {
        requirePresentationFields(quantity, ["state"], path);
        return Object.freeze({ state: "unknown" });
    }
    if (quantity.state === "unavailable") {
        const unavailable = requirePresentationFields(
            quantity,
            ["reason", "state"],
            path,
        );
        return Object.freeze({
            state: "unavailable",
            reason: parseUnavailableReason(unavailable.reason, `${path}.reason`),
        });
    }
    throw new CalculatorPresentationContractError(
        `${path}.state`,
        "expected known, unknown, or unavailable",
    );
}

export function parseCalculatorExpectedItemQuantityV1(
    value,
    path = "expectedQuantity",
) {
    const expected = requirePresentationRecord(value, path);
    if (expected.state === "known") {
        const known = requirePresentationFields(expected, ["state", "unit", "value"], path);
        if (known.unit !== "count") {
            throw new CalculatorPresentationContractError(`${path}.unit`, "expected count");
        }
        return Object.freeze({
            state: "known",
            unit: "count",
            value: requirePresentationFinite(known.value, `${path}.value`, { minimum: 0 }),
        });
    }
    if (expected.state === "unknown") {
        requirePresentationFields(expected, ["state"], path);
        return Object.freeze({ state: "unknown" });
    }
    if (expected.state === "unavailable") {
        const unavailable = requirePresentationFields(
            expected,
            ["reason", "state"],
            path,
        );
        return Object.freeze({
            state: "unavailable",
            reason: parseUnavailableReason(unavailable.reason, `${path}.reason`),
        });
    }
    throw new CalculatorPresentationContractError(
        `${path}.state`,
        "expected known, unknown, or unavailable",
    );
}

function parsedQuantityIsPresentationRelevant(quantity) {
    return quantity.state !== "known"
        || quantity.minimum !== 1
        || quantity.maximum !== 1;
}

/**
 * Exact one is the identity multiplier. It remains a real calculator fact,
 * but an otherwise uneventful 1..1 history adds no useful loot presentation.
 */
export function calculatorItemQuantityIsPresentationRelevant(
    quantityValue,
    path = "quantity",
) {
    return parsedQuantityIsPresentationRelevant(
        parseCalculatorItemQuantityV1(quantityValue, path),
    );
}

/**
 * Keep a quantity history visible when any candidate changes the outcome or
 * records an unresolved state. This still exposes a meaningful overwrite to
 * 1..1 while suppressing histories made entirely from identity multipliers.
 */
export function calculatorItemQuantityHistoryIsPresentationRelevant(
    candidatesValue,
    path = "candidates",
) {
    const relevance = requirePresentationArray(candidatesValue, path)
        .map((candidateValue, candidateIndex) => {
            const candidatePath = `${path}[${candidateIndex}]`;
            const candidate = requirePresentationRecord(candidateValue, candidatePath);
            const operation = requirePresentationRecord(
                candidate.operation,
                `${candidatePath}.operation`,
            );
            const kind = requirePresentationString(
                operation.kind,
                `${candidatePath}.operation.kind`,
            );
            if (kind !== "itemQuantity" && kind !== "itemQuantityDefault") {
                throw new CalculatorPresentationContractError(
                    `${candidatePath}.operation.kind`,
                    "expected itemQuantity or itemQuantityDefault",
                );
            }
            return calculatorItemQuantityIsPresentationRelevant(
                operation.quantity,
                `${candidatePath}.operation.quantity`,
            );
        });
    return relevance.some(Boolean);
}

/**
 * Formats only source/overlay-authored quantity semantics. A range is never
 * converted to an expected value here; EV is shown only when the engine
 * provides a separate typed Known expectedQuantity.
 */
export function calculatorItemQuantityDisplay(
    quantityValue,
    expectedQuantityValue,
    { locale = "en-US", path = "item" } = {},
) {
    const quantity = parseCalculatorItemQuantityV1(
        quantityValue,
        `${path}.quantity`,
    );
    const quantityDisplay = calculatorItemQuantityRangeDisplay(
        quantity,
        { locale, path: `${path}.quantity` },
    );
    const expected = parseCalculatorExpectedItemQuantityV1(
        expectedQuantityValue,
        `${path}.expectedQuantity`,
    );
    if (quantityDisplay === null || !parsedQuantityIsPresentationRelevant(quantity)) {
        return null;
    }
    let formatter;
    try {
        formatter = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
    } catch (_error) {
        throw new CalculatorPresentationContractError(`${path}.locale`, "invalid locale");
    }
    if (
        expected.state !== "known"
        || quantity.state !== "known"
        || quantity.minimum === quantity.maximum
    ) {
        return quantityDisplay;
    }
    const expectedText = formatter.format(expected.value);
    const expectedShort = t("calculator.data.quantity.expected_short", "EV");
    return Object.freeze({
        text: `${quantityDisplay.text} · ${expectedShort} ${expectedText}`,
        ariaLabel: `${quantityDisplay.ariaLabel}. ${t(
            "calculator.data.quantity.accessible_expected",
            "Expected quantity { $quantity }",
            { quantity: expectedText },
        )}`,
    });
}

export function calculatorItemQuantityRangeDisplay(
    quantityValue,
    { locale = "en-US", path = "quantity" } = {},
) {
    const quantity = parseCalculatorItemQuantityV1(quantityValue, path);
    if (quantity.state !== "known") {
        return null;
    }
    let formatter;
    try {
        formatter = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
    } catch (_error) {
        throw new CalculatorPresentationContractError(`${path}.locale`, "invalid locale");
    }
    const minimum = formatter.format(quantity.minimum);
    const maximum = formatter.format(quantity.maximum);
    const rangeText = quantity.minimum === quantity.maximum
        ? `×${minimum}`
        : `×${minimum}–${maximum}`;
    const rangeAccessible = quantity.minimum === quantity.maximum
        ? t("calculator.data.quantity.accessible_exact", "Quantity { $quantity }", {
            quantity: minimum,
        })
        : t(
            "calculator.data.quantity.accessible_range",
            "Quantity { $minimum } to { $maximum }",
            { minimum, maximum },
        );
    return Object.freeze({ text: rangeText, ariaLabel: rangeAccessible });
}
